import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import { IAddOn, IGlassesPackage } from '../../types';
import { PriceLine } from '../PriceLine';
import { PriceDivider } from './PriceDivider';
import { breakpoints, colors } from '../../constants';
import { GlassesIllustration } from '../GlassesIllustration';
import { getPricingDetails, getQSVar } from '../../utils';

interface IProps {
    className?: string;
    selectedPackage: IGlassesPackage;
    selectedAddOns?: IAddOn[];
}

const MainSection = styled.div`
    padding: 30px 30px 65px;
    background-color: ${colors.white};
`;

const BasePricingOverview = ({
    className,
    selectedPackage,
    selectedAddOns,
}: IProps) => {
    const { t } = useTranslation();
    const pricingDetails = getPricingDetails(selectedPackage, selectedAddOns);

    if (!pricingDetails) return null;

    const { framePrice, glassesPrice, discount, subTotal, total } =
        pricingDetails;

    const glassesType = selectedPackage.multifocal
        ? t('multifocal')
        : t('single');

    const frameName = getQSVar('name') || '';

    const priceFrom = subTotal !== total ? subTotal : undefined;

    return (
        <div className={className}>
            <MainSection>
                <h3>{t('overview')}</h3>
                <PriceDivider />
                <PriceLine
                    item={`${t('frame')} ${frameName}`}
                    price={framePrice}
                />
                <PriceLine
                    item={`${glassesType} ${selectedPackage.name}`}
                    price={glassesPrice}
                />
                {selectedAddOns &&
                    selectedAddOns.map((selectedAddOn) => {
                        const substring = '- niet combineerbaar';

                        const addOnName = selectedAddOn.name.includes(substring)
                            ? selectedAddOn.name.replace(substring, '')
                            : selectedAddOn.name;

                        return (
                            <PriceLine
                                included={selectedAddOn.priceInt === 0}
                                item={addOnName}
                                key={selectedAddOn.id}
                                price={selectedAddOn.priceInt}
                            />
                        );
                    })}
                {discount > 0 && (
                    <>
                        <PriceDivider />
                        <PriceLine item={t('glassesPrice')} price={subTotal} />
                        <PriceLine
                            bold
                            highlight
                            isDiscount
                            item={t('discount')}
                            price={discount}
                        />
                    </>
                )}
                <PriceDivider />
                <PriceLine
                    bold
                    isTotal
                    item={t('youPay')}
                    price={total}
                    priceFrom={priceFrom}
                />
                {selectedPackage.imageUrl && (
                    <GlassesIllustration selectedPackage={selectedPackage} />
                )}
            </MainSection>
        </div>
    );
};

export const PricingOverview = styled(BasePricingOverview)`
    margin-bottom: 20px;

    @media (${breakpoints.mediumMin}) {
        flex: 0 0 55%;
        margin-right: 50px;
        margin-bottom: 0;
    }

    h3 {
        font-size: 20px;
        margin-bottom: 15px;
    }
`;
